import styles from './Argument.module.css';
import { FlipCard, useBeat, useReducedMotionAware } from './FlipCard.js';

/**
 * The page's argument, made with the product's own object rather than a claim.
 *
 * The same four people hold the same four honest reads on both sides. On the
 * left somebody says a number out loud and you watch three cards turn over to
 * match it. On the right the same four commit face-down and reveal a real
 * spread. Only the right side carries state colour: under the State-Only Rule,
 * nothing was learned on the left, so there is nothing to report.
 */

interface Seat {
  readonly name: string;
  /** What this person actually thinks. */
  readonly honest: string;
  /** The one who speaks first, and so never changes their answer. */
  readonly speaksFirst?: boolean;
}

const SEATS: readonly Seat[] = [
  { name: 'Mara', honest: '8', speaksFirst: true },
  { name: 'Dev', honest: '3' },
  { name: 'Iris', honest: '13' },
  { name: 'Tom', honest: '8' },
];

const ANCHOR = '8';
const EXTREMES = new Set(['3', '13']);

export function PlanningArgument() {
  const animate = useReducedMotionAware();
  const beat = useBeat(animate);
  const shown = beat === 'shown';

  return (
    <div className={styles.argument}>
      <section className={styles.pane}>
        <h2 className={styles.paneLabel}>Estimating out loud</h2>

        <div className={styles.seats}>
          {SEATS.map((seat, index) => (
            <span key={seat.name} className={styles.seat}>
              <FlipCard
                front={seat.honest}
                back={ANCHOR}
                flipped={!seat.speaksFirst && shown}
                delayMs={seat.speaksFirst ? 0 : 220 + index * 160}
              />
              <span className={styles.seatName}>{seat.name}</span>
              <span className={styles.seatNote}>
                {seat.speaksFirst ? 'says it first' : 'follows'}
              </span>
            </span>
          ))}
        </div>

        <p className={styles.paneCopy}>
          One voice says a number and the room lands on it. What reaches the
          tracker is one person&rsquo;s guess wearing everyone&rsquo;s name.
        </p>
      </section>

      <div className={styles.divide} aria-hidden="true" />

      <section className={styles.pane}>
        <h2 className={styles.paneLabel}>Estimating face-down</h2>

        <div className={styles.seats}>
          {SEATS.map((seat, index) => (
            <span key={seat.name} className={styles.seat}>
              <FlipCard
                front=""
                back={seat.honest}
                flipped={shown}
                delayMs={index * 110}
                facedownFront
                {...(EXTREMES.has(seat.honest)
                  ? { tone: 'split' as const }
                  : {})}
              />
              <span className={styles.seatName}>{seat.name}</span>
              <span className={styles.seatNote}>
                {shown ? 'played' : 'ready'}
              </span>
            </span>
          ))}
        </div>

        <p className={styles.paneCopy}>
          Everyone commits first, then all four turn over together.{' '}
          <strong>Three against thirteen</strong> is not a bad estimate &mdash;
          it is two people who read the work differently, and the ten minutes
          that fixes it.
        </p>
      </section>
    </div>
  );
}
